import { FormArray } from '@angular/forms';
import { fixNullValuesInMarkCode } from '@examdojo/markscheme-v2';
import { ContentItem, ContentItemTypeEnum } from '@examdojo/models/content-item';
import { StemMarkScheme } from '@examdojo/models/markscheme-v2';
import {
  OptionsItem,
  OptionsMarkscheme,
  QuestionItemTypeEnum,
  QuestionItemV2,
  StemGroupItemV2,
  StemItemV2,
  StimulusItemV2,
} from '@examdojo/models/question';
import cloneDeep from 'lodash/cloneDeep';
import { TopLevelFormGroup } from './question-item-editor.model';

export type QuestionItemsEditorFormValue = ReturnType<FormArray<TopLevelFormGroup>['getRawValue']>;

/**
 * Gets the form data without optional properties.
 * @param formValue - The form value to get the data from.
 * @returns The form data without optional properties.
 */
export function getFormData(formValue: QuestionItemsEditorFormValue | QuestionItemV2[]): QuestionItemV2[] {
  return formValue.map((questionItem) => {
    switch (questionItem.type) {
      case QuestionItemTypeEnum.Stem:
        return {
          ...questionItem,
          markscheme: getMarkschemeData(questionItem.markscheme),
          content_items: getContentItemsData(questionItem.content_items),
        } satisfies StemItemV2;
      case QuestionItemTypeEnum.Stimulus:
        return {
          ...questionItem,
          content_items: getContentItemsData(questionItem.content_items),
        } satisfies StimulusItemV2;
      case QuestionItemTypeEnum.StemGroup:
        return {
          ...questionItem,
          stems: questionItem.stems.map((stem) => ({
            ...stem,
            content_items: getContentItemsData(stem.content_items),
            markscheme: getMarkschemeData(stem.markscheme),
          })),
          content_items: getContentItemsData(questionItem.content_items),
        } satisfies StemGroupItemV2;
      case QuestionItemTypeEnum.Options:
        // Extract the basic markscheme data (without worked_solution)
        const { marks, correct_option_indexes, max_selection_count, min_selection_count, topic_ids } =
          questionItem.markscheme;

        // Create the processed markscheme with proper typing
        const processedMarkscheme: OptionsMarkscheme = {
          marks,
          correct_option_indexes,
          ...(max_selection_count !== undefined ? { max_selection_count } : {}),
          ...(min_selection_count !== undefined ? { min_selection_count } : {}),
          topic_ids,
          ...(questionItem.markscheme.worked_solution
            ? { worked_solution: getContentItemsData(questionItem.markscheme.worked_solution) }
            : {}),
        };

        return {
          ...questionItem,
          options: questionItem.options.map((option) => ({
            ...option,
            content_items: getContentItemsData(option.content_items),
          })),
          markscheme: processedMarkscheme,
          content_items: getContentItemsData(questionItem.content_items),
        } satisfies OptionsItem;

      default:
        return questionItem;
    }
  });
}

function getMarkschemeData(markscheme: StemMarkScheme | null): StemMarkScheme | null {
  if (!markscheme) {
    return null;
  }

  return {
    ...markscheme,
    all_mark_groups: markscheme.all_mark_groups.map((markGroup) => ({
      ...markGroup,
      criterion: getContentItemsData(markGroup.criterion),
      mark_code: fixNullValuesInMarkCode(markGroup.mark_code) ?? '',
    })),
    solution_paths: markscheme.solution_paths.map((solutionPath) => ({
      ...solutionPath,
      worked_solution: getContentItemsData(solutionPath.worked_solution),
    })),
  };
}

function getContentItemsData(contentItems: ContentItem[]): ContentItem[] {
  // Delete all optional properties that are null
  return contentItems.map((contentItem) => {
    const item = cloneDeep(contentItem);

    // Handle image content items
    if (item.type === ContentItemTypeEnum.Image) {
      if (item.not_to_scale === null) {
        delete item.not_to_scale;
      }
      if (item.fill_in_the_blanks === null) {
        delete item.fill_in_the_blanks;
      }
      // TODO: make sure that these are not needed.
      if (item.title === null) {
        delete item.title;
      }
      if (item.style === null) {
        delete item.style;
      }
      if (item.container_width === null) {
        delete item.container_width;
      }
    }

    if (item.type === ContentItemTypeEnum.Table) {
      // Handle null string values
      if (item.title === null) {
        delete item.title;
      }
      if (item.description === null) {
        delete item.description;
      }
      if (item.container_width === null) {
        delete item.container_width;
      }
      if (item.style === null) {
        delete item.style;
      }
      if (item.fill_in_the_blanks === null) {
        delete item.fill_in_the_blanks;
      }
    }

    return item;
  });
}

export enum QuestionItemsSource {
  QuestionItems = 'Question items',
  JsonEditor = 'JSON Editor',
  HistoryManager = 'History Manager',
  Storage = 'Storage',
}
